import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartErrorBoundary } from '@/components/chart-error-boundary/ChartErrorBoundary';
import { computeSharePurchaseTrend } from '@equityUtils';
import type { SecuritiesTransaction } from '@/hooks/securities/useSecuritiesTransactions.ts';
import { format, parseISO } from 'date-fns';

interface SharePurchaseTrendChartProps {
  transactions: SecuritiesTransaction[];
  isLoading: boolean;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: { date: string; cumulativeShares: number } }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;

  const { date, cumulativeShares } = payload[0].payload;
  const formattedDate = format(parseISO(date), 'MMM dd, yyyy');

  return (
    <div className="bg-background border border-border rounded-lg shadow-lg p-3">
      <p className="text-sm text-muted-foreground mb-1">{formattedDate}</p>
      <p className="text-base font-semibold">
        {cumulativeShares.toFixed(4)} <span className="text-sm font-normal text-muted-foreground">shares</span>
      </p>
    </div>
  );
}

/**
 * SharePurchaseTrendChart renders a cumulative line chart of shares owned over time.
 *
 * - Requires at least two data points to draw a meaningful trend line.
 * - Aggregates same-date deposit transactions before computing the running total.
 * - Y-axis values are displayed with up to 4 decimal places to handle fractional shares.
 */
export function SharePurchaseTrendChart({ transactions, isLoading }: SharePurchaseTrendChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Share Purchase Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="h-[300px] animate-pulse bg-muted rounded"
            aria-busy="true"
            aria-label="Loading share purchase trend chart"
          />
        </CardContent>
      </Card>
    );
  }

  const trendData = computeSharePurchaseTrend(
    transactions.map((tx) => ({
      transaction_date: tx.transaction_date,
      amount: tx.amount,
    }))
  );

  if (trendData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Share Purchase Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-sm text-muted-foreground">No transactions found</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (trendData.length === 1) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Share Purchase Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-sm text-muted-foreground text-center">
              More transaction history needed to display trend
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Share Purchase Trend</CardTitle>
      </CardHeader>
      <CardContent>
        <ChartErrorBoundary fallbackTitle="Share Purchase Trend">
          <div
            className="w-full overflow-x-auto"
            role="img"
            aria-label={`Share purchase trend chart showing cumulative shares over ${trendData.length} data points`}
          >
            <div className="min-w-[400px]">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(v) =>
                      format(parseISO(v), 'MMM d, yyyy')
                    }
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tickFormatter={(v: number) => v.toFixed(4)}
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={70}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="cumulativeShares"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </ChartErrorBoundary>

        {/* Screen-reader accessible data table */}
        <div className="sr-only">
          <table>
            <caption>Share purchase trend data</caption>
            <thead>
              <tr>
                <th>Date</th>
                <th>Cumulative Shares</th>
              </tr>
            </thead>
            <tbody>
              {trendData.map((entry) => (
                <tr key={entry.date}>
                  <td>{format(parseISO(entry.date), 'MMM dd, yyyy')}</td>
                  <td>{entry.cumulativeShares.toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
